import storeManager from '../../store/StoreManager';
import serviceWorkerCacheHandler from './serviceWorkerCacheHandler';
import domCacheHandler from './domCacheHandler';
import mediaFinder from './mediaFinder';
import generalResourcesCacher from './generalResourcesCacher';
import ServiceWorkerService from '../../services/ServiceWorkerService';
import settingsReader from '../../store/readHelpers/SettingsReader';
import cacheStatusActions, { cacheStatusTypes, cacheResultTypes } from '../../actions/CacheStatusActions';
import QuestionDataIdentifier from '../../services/sessionParameters/QuestionDataIdentifier';
import * as sessionConstants from '../../constants/SessionConstants';
import AsyncRetryer from '../../services/AsyncRetryer';
import utilService from '../../services/UtilService';
import mediaResourcesActions from '../../actions/MediaResourcesActions';
import constants from '../../qti/Constants';
import loadProgressHandler from './loadProgressHandler';
import backgroundAudioService from '../../services/BackgroundAudioService';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';

export const cacheContexts = {
    INFO_DOCS: 'info_docs',
    GENERAL_RESOURCES: 'general_resources',
    READINESS_CHECK: 'readiness_check',
    TEST: 'test',
};

export function dispatchCachedSource(identifier, url) {
    storeManager.dispatch(
        mediaResourcesActions.setCachedSource(identifier, url)
    );
}

const cacheManager = {
    cacheHandler: null,
    abortController: null,
    getAbortController() {
        try {
            if (!this.abortController) {
                this.abortController = new AbortController();
            }
            return this.abortController;
        } catch (error) {
            console.error('AbortController not supported', error);
            return null;
        }
    },
    hasAborted() {
        return !!this.getAbortController()?.signal?.aborted;
    },

    getAllAssessmentRunIds() {
        const { assessment, pendingAssessments } = storeManager.getState();
        const list = Object.keys(pendingAssessments);
        if (assessment.assessmentRunId) {
            list.push(assessment.assessmentRunId);
        }

        return list;
    },

    isCacheable(file) {
        return !utilService.isDataURL(file.url);
    },

    cacheIsDone(identifier, { cacheStatus } = storeManager.getState()) {
        return cacheStatus[identifier]?.status === cacheStatusTypes.DONE;
    },

    allCacheItemsAreDone() {
        const { cacheStatus } = storeManager.getState();
        return Object.values(cacheStatus).every(item => item.status === cacheStatusTypes.DONE);
    },

    reportCacheStatus(identifier, status, result) {
        storeManager.dispatch(
            cacheStatusActions.setStatus(identifier, status, result)
        );
    },

    /**
     * Caches both readiness check and test resources. All media to be used will be loaded and cached
     */
    async cacheQuestionSetMedia(questionSet, cacheContext) {
        const { assessmentRunId, contentRevisionId } = questionSet;
        const { AUDIO, FILE } = constants.additionalResourceType;
        const questionDataIdentifier = new QuestionDataIdentifier(sessionConstants.MODE_QUESTION_SET, { assessmentRunId, contentRevisionId });
        const startTime = Date.now();
        console.log('cacheQuestionSetMedia start', cacheContext, questionDataIdentifier.paramValue);
        const mediaModels = mediaFinder
            .getMediaModelsInQuestionSet(questionSet, [FILE, AUDIO])
            .filter(this.isCacheable);

        this.reportCacheStatus(questionDataIdentifier.paramValue, cacheStatusTypes.IN_PROGRESS, '');
        try {
            const cachedItems = await Promise.all(
                mediaModels.map(mediaModel => this.cacheMediaModelWithRetries(mediaModel, cacheContext, questionDataIdentifier))
            );

            const durationSeconds = Math.round((Date.now() - startTime) / 1000);
            console.log(`cacheQuestionSetMedia done in ${durationSeconds} seconds`, cacheContext, questionDataIdentifier.paramValue);

            if (marketplacePropertiesService.isBackgroundAudioCheckEnabled() && backgroundAudioService.hasBackgroundAudio(assessmentRunId)) {
                await backgroundAudioService.nudgePlayerInstanceCreation(assessmentRunId);
            }

            const maxTries = mediaModels.length === 0 ? 1 : Math.max(...cachedItems.map(item => item.startedTries));
            const downloadTime = mediaModels.length === 0 ? 0 : Date.now() - startTime;

            loadProgressHandler.reportDownloadInfo(assessmentRunId, cacheContext, true, maxTries, downloadTime);
            this.reportCacheStatus(questionDataIdentifier.paramValue, cacheStatusTypes.DONE, cacheResultTypes.COMPLETE);

            return Promise.resolve(`Cached question set AR ${assessmentRunId} CR ${contentRevisionId}`);
        } catch (error) {
            const {
                message,
                startedTries = 0,
            } = error;
            const resultType = this.hasAborted() ? cacheResultTypes.TIMEOUT : cacheResultTypes.INCOMPLETE;
            loadProgressHandler.reportDownloadInfo(assessmentRunId, cacheContext, false, startedTries);
            this.reportCacheStatus(questionDataIdentifier.paramValue, cacheStatusTypes.DONE, resultType);

            console.log(message);
            return Promise.reject(`Failed caching question set AR ${assessmentRunId} CR ${contentRevisionId}`);
        }
    },

    async cacheChildTestInfoDocs(childTests) {
        const startTime = Date.now();
        const { parentTest } = storeManager.getState();
        const questionDataIdentifier = parentTest.sourceTemplateId
            ? new QuestionDataIdentifier('info_docs', { sourceTemplateId: parentTest.sourceTemplateId })
            : new QuestionDataIdentifier('info_docs', { assessmentRunId: parentTest.assessment.assessmentRunId });

        console.log('cacheChildTestInfoDocs start', questionDataIdentifier.paramValue);
        const files = childTests
            .map(childTest => mediaFinder.getMediaModelsInQuestion(childTest.documentModel, childTest.assessmentRunId))
            .reduce((accumulator, models) => {
                return accumulator.concat(models);
            }, [])
            .filter(this.isCacheable);

        try {
            await Promise.all(
                files.map(file => this.cacheMediaModelWithRetries(file, cacheContexts.INFO_DOCS, questionDataIdentifier))
            );

            const durationSeconds = Math.round((Date.now() - startTime) / 1000);
            console.log(`cacheChildTestInfoDocs done in ${durationSeconds} seconds`, questionDataIdentifier.paramValue);

            this.reportCacheStatus(questionDataIdentifier.paramValue, cacheStatusTypes.DONE, cacheResultTypes.COMPLETE);

            return Promise.resolve(`Cached info docs ID ${questionDataIdentifier.paramValue}`);
        } catch (error) {
            this.reportCacheStatus(questionDataIdentifier.paramValue, cacheStatusTypes.DONE, cacheResultTypes.INCOMPLETE);
            return Promise.reject(`Failed caching info docs ID ${questionDataIdentifier.paramValue}`);
        }
    },

    async cacheGeneralResources(assessment) {
        const filePackageCacher = filePackage => this.cacheFilePackage(filePackage, cacheContexts.GENERAL_RESOURCES, assessment.contentRevisionId);
        return generalResourcesCacher.cacheAll(assessment, filePackageCacher);
    },

    async cacheFilePackage(filePackage, cacheContext, questionDataIdentifier) {
        return this
            .getCacheHandler()
            .cacheFilePackage(filePackage, cacheContext, questionDataIdentifier);
    },

    async cacheSingleFileModel(fileModel, cacheContext, questionDataIdentifier) {
        return this
            .getCacheHandler()
            .cacheSingleFile(fileModel, cacheContext, questionDataIdentifier);
    },

    abortResourceLoading() {
        const abortController = this.getAbortController();
        if (abortController) {
            abortController.abort();
        }
        this.getCacheHandler().abortResourceLoading();
    },

    async cacheMediaModel(mediaModel, cacheContext, questionDataIdentifier) {
        const { alternativeUrl, logicalName, url, sources = [] } = mediaModel;
        const urls = [url, alternativeUrl]
            .concat(sources.map(source => source.url))
            .filter(urlString => !!urlString);

        let result;
        for (let i = 0; i < urls.length; i++) {
            // eslint-disable-next-line no-await-in-loop
            result = await this.cacheSingleFileModel({ ...mediaModel, url: urls[i] }, cacheContext, questionDataIdentifier);
            if (result.success === true) {
                dispatchCachedSource(logicalName, url);
                return { ...mediaModel, ...result, cachedUrl: urls[i] };
            }
        }
        if (this.hasAborted()) {
            return Promise.reject(new DOMException('AbortError'));
        }

        return Promise.reject(`Unable to cache media model with logicalName: ${logicalName}`);
    },

    async cacheMediaModelWithRetries(mediaModel, cacheContext, questionDataIdentifier) {
        const functionToTry = () => this.cacheMediaModel(mediaModel, cacheContext, questionDataIdentifier);
        const retryer = new AsyncRetryer(functionToTry, 5, 10000, true, true);
        return retryer.start();
    },

    getCacheHandler() {
        if (this.cacheHandler !== null) {
            return this.cacheHandler;
        }

        if (document.location.search.includes('cacheHandler=sw') && ServiceWorkerService.isControlledByServiceWorker()) {
            this.cacheHandler = serviceWorkerCacheHandler.init();
        } else if (document.location.search.includes('cacheHandler=dom')) {
            this.cacheHandler = domCacheHandler;
        } else if (ServiceWorkerService.isControlledByServiceWorker() && settingsReader.isServiceWorkerCacheEnabled()) {
            this.cacheHandler = serviceWorkerCacheHandler.init();
        } else {
            this.cacheHandler = domCacheHandler;
        }

        console.log('CacheHandler used: ', this.cacheHandler.name);
        return this.cacheHandler;
    },

    usesServiceWorkerCache() {
        return this.getCacheHandler().name === serviceWorkerCacheHandler.name;
    },

    async isReadinessCheckQuestionSetAlreadyCached(contentRevisionId) {
        return serviceWorkerCacheHandler
            .getExistingCacheNameForReadinessCheck(contentRevisionId)
            .then(name => !!name);
    },

    async makeRoomForANewReadinessCheckCache() {
        return serviceWorkerCacheHandler.makeRoomForANewReadinessCheckCache();
    },
};

export default cacheManager;
